import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/auth-context";
import { useCreateChallenge, useMyChallenges } from "@/hooks/use-challenges";
import { getErrorMessage } from "@/utils/error-message";

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function Challenges() {
  const { user } = useAuth();
  const { data: challenges, isLoading, error } = useMyChallenges();
  const createChallenge = useCreateChallenge();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyError, setCopyError] = useState(false);

  const handleCreate = async () => {
    const result = await createChallenge.mutateAsync(5);
    const url = `${window.location.origin}/challenge/${result.challenge.challengeId}`;
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopiedId(result.challenge.challengeId);
      setTimeout(() => setCopiedId(null), 3000);
    } else {
      setCopyError(true);
      setTimeout(() => setCopyError(false), 3000);
    }
  };

  const handleCopyLink = async (challengeId: string) => {
    const url = `${window.location.origin}/challenge/${challengeId}`;
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopiedId(challengeId);
      setTimeout(() => setCopiedId(null), 3000);
    } else {
      setCopyError(true);
      setTimeout(() => setCopyError(false), 3000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-700">Challenges</h1>
        <Button
          onClick={handleCreate}
          disabled={createChallenge.isPending}
        >
          {createChallenge.isPending ? "Creating..." : "New Challenge"}
        </Button>
      </div>

      <Card className="bg-navy-50">
        <p className="text-sm text-navy-700">
          Create a challenge with 5 random scenarios and share the link with a friend.
          Compare results side by side!
        </p>
      </Card>

      {isLoading && <LoadingSpinner message="Loading challenges..." />}
      {error && <ErrorMessage message={getErrorMessage(error)} />}
      {copyError && <ErrorMessage message="Unable to copy link — please copy the URL manually." />}

      {challenges && challenges.length > 0 && (
        <div className="space-y-3">
          {challenges.map((ch) => {
            const resultEntries = Object.values(ch.results);
            const myResult = ch.results[user?.email ?? ""];

            return (
              <Card key={ch.challengeId}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {ch.scenarioIds.length}-Question Challenge
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {new Date(ch.createdAt).toLocaleDateString()}
                    </p>
                    {resultEntries.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {resultEntries
                          .sort((a, b) => b.correct - a.correct)
                          .map((r) => (
                            <div key={r.userId} className="flex items-center gap-2 text-xs">
                              <span className={`font-medium ${r.userId === user?.email ? "text-navy-700" : "text-gray-700"}`}>
                                {r.displayName}{r.userId === user?.email ? " (you)" : ""}
                              </span>
                              <span className="text-gray-400">
                                {r.correct}/{r.total} correct ({Math.round((r.correct / r.total) * 100)}%)
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                    {!myResult && (
                      <a
                        href={`/challenge/${ch.challengeId}`}
                        className="mt-2 inline-flex min-h-[44px] items-center rounded-lg bg-navy-50 px-3 text-sm font-medium text-navy-600 hover:bg-navy-100 hover:text-navy-800"
                      >
                        Take this challenge &rarr;
                      </a>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCopyLink(ch.challengeId)}
                  >
                    {copiedId === ch.challengeId ? "Copied!" : "Share"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {challenges && challenges.length === 0 && (
        <Card className="text-center">
          <p className="text-gray-500">No challenges yet. Create one and share it with friends!</p>
        </Card>
      )}
    </div>
  );
}
